import { useMemo } from "react";
import { useAuth } from "react-oidc-context";

import { withPreview } from "@/lib/preview";

export type ApiFetch = <T>(path: string, init?: RequestInit) => Promise<T>;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly runId?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export function useRestClient(): ApiFetch {
  const { user } = useAuth();
  // Empty deployment configuration keeps API traffic on the current portal origin.
  const base = (import.meta.env.VITE_API_ENDPOINT as string | undefined) || window.location.origin;
  const token = user?.access_token ?? "";

  return useMemo((): ApiFetch => {
    return async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
      // Read at each call: a preview started or stopped applies to the next request.
      const response = await fetch(withPreview(`${base}${path}`), {
        ...init,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...init?.headers,
        },
      });
      if (!response.ok) {
        let detail = "";
        let runId: string | undefined;
        try {
          const body: unknown = await response.json();
          if (
            body &&
            typeof body === "object" &&
            "run_id" in body &&
            typeof body.run_id === "string"
          )
            runId = body.run_id;
          if (
            body &&
            typeof body === "object" &&
            "error" in body &&
            typeof body.error === "string"
          ) {
            detail = body.error;
          } else if (body && typeof body === "object" && "detail" in body) {
            const value = body.detail;
            detail =
              typeof value === "string"
                ? value
                : Array.isArray(value)
                  ? value
                      .map(
                        (entry: { loc?: unknown; msg?: unknown }) =>
                          `${Array.isArray(entry.loc) ? entry.loc.join(".") : "request"}: ${String(entry.msg ?? "invalid")}`,
                      )
                      .join("; ")
                  : "";
          }
        } catch {
          /* Keep the HTTP status when no structured error is available. */
        }
        throw new ApiError(
          response.status,
          detail || response.statusText || "The request could not be completed. Please try again.",
          runId,
        );
      }

      if (response.status === 204) {
        return undefined as T;
      }

      const text = await response.text();
      if (!text) {
        return undefined as T;
      }

      return JSON.parse(text) as T;
    };
  }, [base, token]);
}
